import { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Grid, Typography, Container } from '@mui/material';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { useApiStore } from '@core/store';
import { eventBus, EventTypes } from '@core/pubsub/EventBus';
import { NewPageTitleBar } from '@/components';
import { getMenuData } from '@core/data/menuData';
import { FCSecurePage, useRegisterRefresh, useShopPersistence } from '@/components/shared/navigation/securePage.ts';

import FastLanerHealthState from '@/components/widgets/FastLanerHealthState';
import FastLanerBatteryState from '@/components/widgets/FastLanerBatteryState';
import FastLanerCharging from '@/components/widgets/FastLanerCharging';
import ShopSelector from '@/components/selectors/ShopSelector.tsx';

const FastLanerOverview: FCSecurePage = () => {
    const { t } = useTranslation();
    const [lastUpdate, setLastUpdate] = useState<DateTime | undefined>(undefined);
    const reloadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const { shopIds } = useApiStore();
    const { selectedShopIds, setSelectedShopIds } = useShopPersistence();

    const reloadMinutes = 5;
    const widgetColClasses = { xl: 4, lg: 4, md: 4, sm: 6, xs: 12 };

    useEffect(() => {
        if (selectedShopIds) {
            reloadWidgets();
        }
    }, []);

    const reloadWidgets = useCallback(async () => {
        try {
            eventBus.emit(EventTypes.WIDGETS_RELOAD);
            setLastUpdate(DateTime.now());
        } catch (error) {
            console.error('Error reloading widgets:', error);
        }
    }, [selectedShopIds]);

    useRegisterRefresh(reloadWidgets);

    const startReloadTimer = useCallback(() => {
        if (reloadTimerRef.current) {
            clearTimeout(reloadTimerRef.current);
        }
        reloadTimerRef.current = setTimeout(
            () => {
                reloadWidgets();
                startReloadTimer();
            },
            reloadMinutes * 60 * 1000
        );
    }, [reloadMinutes, reloadWidgets]);

    useEffect(() => {
        startReloadTimer();
        return () => {
            if (reloadTimerRef.current) {
                clearTimeout(reloadTimerRef.current);
            }
        };
    }, [startReloadTimer]);

    const sortedShops = [...shopIds]
        .filter((s) => s.fastLanerGroupIds && s.fastLanerGroupIds.length > 0)
        .sort((a, b) => a.name.localeCompare(b.name));

    const selectedShop = shopIds.find((s) => s.id === selectedShopIds[0]) ?? null;

    return (
        <Box sx={{ width: '100%', bgcolor: 'background.default' }}>
            <NewPageTitleBar
                icon={<i className="bi bi-cart3" style={{ fontSize: '16px' }} />}
                title={`${t('fastLanerOverview')}`}
                iconSize="18px"
                menus={getMenuData()}
                currentRoute={{ path: '/statistics/fastLaner/overview' }}
                t={t}
                lastUpdate={lastUpdate}
                hasAutoRefresh={true}
            />

            <Container maxWidth={false} sx={{ py: 1 }}>
                <Box sx={{ mb: 6 }}>
                    <ShopSelector
                        value={selectedShopIds[0] ?? null}
                        onChange={(value) => setSelectedShopIds(value ? [value] : [])}
                        label={t('shop')}
                    />
                </Box>

                {!selectedShopIds && (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                        <Typography variant="h6" color="text.secondary">
                            {t('selectAShop')}
                        </Typography>
                    </Box>
                )}

                {selectedShopIds && selectedShop && (
                    <>
                        {sortedShops.length === 0 ? (
                            <Box sx={{ textAlign: 'center', py: 8 }}>
                                <Typography variant="h6" color="text.secondary">
                                    {t('noData')}
                                </Typography>
                            </Box>
                        ) : (
                            <Box key={selectedShop.id} sx={{ mb: 4 }}>
                                <Grid container spacing={3}>
                                    <Grid item {...widgetColClasses}>
                                        <FastLanerHealthState
                                            shopIdName={selectedShop}
                                            heading={t('fastLanerHealthState')}
                                        />
                                    </Grid>
                                    <Grid item {...widgetColClasses}>
                                        <FastLanerBatteryState
                                            shopIdName={selectedShop}
                                            heading={t('fastLanerBatteryState')}
                                        />
                                    </Grid>
                                    <Grid item {...widgetColClasses}>
                                        <FastLanerCharging shopIdName={selectedShop} heading={t('fastLanerCharging')} />
                                    </Grid>
                                </Grid>
                            </Box>
                        )}
                    </>
                )}
            </Container>
        </Box>
    );
};

FastLanerOverview.displayName = 'FastLanerOverview';
export default FastLanerOverview;
